/**
 * SMS Conversation — Chat-style message thread for a prospect.
 * Outbound messages right-aligned (blue), inbound left-aligned (gray).
 */

import { useState, useRef, useEffect } from 'react';
import { Send, Check, CheckCheck, Clock, X, Loader2, PauseCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useMessages, useSendMessage } from '@/lib/api/hooks/useMessages';
import { useSequenceStatus, useSequenceAction } from '@/lib/api/hooks/useProspectTimeline';
import type { SmsMessage } from '@/types/sms';
import { toast } from 'sonner';

interface SmsConversationProps {
  prospectId: string;
  isViewer?: boolean;
}

function DeliveryStatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'delivered':
      return <CheckCheck className="h-3 w-3 text-blue-400" />;
    case 'sent':
      return <Check className="h-3 w-3 text-muted-foreground" />;
    case 'sending':
    case 'queued':
      return <Clock className="h-3 w-3 text-muted-foreground" />;
    case 'failed':
      return <X className="h-3 w-3 text-red-500" />;
    default:
      return <Check className="h-3 w-3 text-muted-foreground" />;
  }
}

function formatTime(timestamp: string): string {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 24) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  if (diffHours < 168) { // 7 days
    return date.toLocaleDateString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function MessageBubble({ message }: { message: SmsMessage }) {
  const isOutbound = message.direction === 'outbound';

  return (
    <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
          isOutbound
            ? 'bg-blue-600 text-white rounded-br-md'
            : 'bg-muted text-foreground rounded-bl-md'
        }`}
      >
        <p className="whitespace-pre-wrap break-words">{message.body}</p>
        <div className={`flex items-center gap-1 mt-1 ${isOutbound ? 'justify-end' : 'justify-start'}`}>
          <span className={`text-[10px] ${isOutbound ? 'text-blue-200' : 'text-muted-foreground'}`}>
            {formatTime(message.created_at)}
          </span>
          {isOutbound && <DeliveryStatusIcon status={message.status} />}
        </div>
      </div>
    </div>
  );
}

export function SmsConversation({ prospectId, isViewer }: SmsConversationProps) {
  const { data, isLoading } = useMessages(prospectId);
  const sendMutation = useSendMessage(prospectId);
  const { data: seqData } = useSequenceStatus(prospectId);
  const sequenceAction = useSequenceAction(prospectId);
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const isPaused = seqData?.sequence?.status === 'paused';

  const handleSequenceAction = async (action: 'resume' | 'opt-out' | 'nurture') => {
    const labels = { resume: 'Resume Sequence', 'opt-out': 'Opt Out', nurture: 'Move to Nurture' };
    try {
      await sequenceAction.mutateAsync(action);
      toast.success(`${labels[action]} applied.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : `Failed to ${action}`);
    }
  };

  const messages = data?.messages || [];

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text) return;

    setDraft('');
    try {
      await sendMutation.mutateAsync(text);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to send SMS');
      setDraft(text); // Restore draft on failure
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-2">Loading messages...</div>;
  }

  return (
    <div className="space-y-2">
      {/* Paused sequence banner + quick actions */}
      {isPaused && (
        <div className="flex flex-col gap-2 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2">
          <div className="flex items-center gap-1.5 text-sm font-medium text-yellow-800">
            <PauseCircle className="h-4 w-4 flex-shrink-0" />
            Sequence paused — prospect replied
          </div>
          {!isViewer && (
            <div className="flex flex-wrap gap-1.5">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-yellow-300 bg-white text-yellow-800 hover:bg-yellow-100"
                onClick={() => handleSequenceAction('resume')}
                disabled={sequenceAction.isPending}
              >
                Resume Sequence
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                onClick={() => handleSequenceAction('nurture')}
                disabled={sequenceAction.isPending}
              >
                Move to Nurture
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-red-200 bg-white text-red-600 hover:bg-red-50"
                onClick={() => handleSequenceAction('opt-out')}
                disabled={sequenceAction.isPending}
              >
                Opt Out
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        className="space-y-2 max-h-64 overflow-y-auto px-1"
      >
        {messages.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No SMS messages yet</p>
        ) : (
          messages.map((msg) => <MessageBubble key={msg.id} message={msg} />)
        )}
      </div>

      {/* Compose */}
      {!isViewer && (
        <div className="flex gap-2 items-end border-t pt-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            className="min-h-[36px] max-h-24 text-sm resize-none"
            rows={1}
          />
          <Button
            size="sm"
            onClick={handleSend}
            disabled={!draft.trim() || sendMutation.isPending}
            className="h-9 w-9 p-0 flex-shrink-0"
          >
            {sendMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
